import { AnalyticsConfig, AnalyticsVisualization } from '../types/analytics.types';

/**
 * Utility to build analytics API endpoints dynamically
 * Mirrors the logic from the OpenAPI generator on the server side
 */
export class AnalyticsEndpointBuilder {
  
  /**
   * Build visualization endpoint path
   * Pattern: /analytics/{configId}/visualizations/{vizId}/data
   */
  static buildVisualizationEndpoint(configId: string, visualizationId: string): string {
    return `/analytics/${configId}/visualizations/${visualizationId}/data`;
  }
  
  /**
   * Build dataset endpoint path  
   * Pattern: /analytics/datasets/{configId}-dataset
   */
  static buildDatasetEndpoint(configId: string): string {
    return `/analytics/datasets/${configId}-dataset`;
  }
  
  /**
   * Build full URL for visualization endpoint
   */
  static buildFullVisualizationUrl(
    baseUrl: string, 
    appKey: string, 
    configId: string, 
    visualizationId: string
  ): string {
    const path = this.buildVisualizationEndpoint(configId, visualizationId);
    return `${baseUrl}/${appKey}${path}`;
  }
  
  /**
   * Build full URL for dataset endpoint
   */
  static buildFullDatasetUrl(
    baseUrl: string, 
    appKey: string, 
    configId: string
  ): string {
    const path = this.buildDatasetEndpoint(configId);
    return `${baseUrl}/${appKey}${path}`;
  }
  
  /**
   * Extract all available endpoints from an analytics configuration
   * This mirrors the server-side logic for what endpoints get generated
   */
  static extractEndpointsFromConfig(config: AnalyticsConfig): {
    visualizations: Array<{
      id: string;
      title: string;
      endpoint: string;
      hasCustomQuery: boolean;
    }>;
    datasets: Array<{
      id: string;
      name: string;
      endpoint: string;
      scope: string;
    }>;
  } {
    const visualizations: Array<{
      id: string;
      title: string;
      endpoint: string;
      hasCustomQuery: boolean;
    }> = [];
    
    const datasets: Array<{
      id: string;
      name: string;
      endpoint: string;
      scope: string;
    }> = [];
    
    // Extract visualizations from tabs (mirrors server logic)
    config.tabs?.forEach(tab => {
      if (tab.visualizations) {
        tab.visualizations.forEach(viz => {
          // Check if this is a standalone visualization (has custom query, no dataset reference)
          const hasCustomQuery = viz.dataQuery && viz.dataQuery.metrics && viz.dataQuery.metrics.length > 0;
          const noDataSetRef = !viz.dataSet || !viz.dataSet.dataSetId;
          
          console.log(`🔧 [EndpointBuilder] Checking visualization ${viz.id}:`, {
            hasCustomQuery,
            noDataSetRef,
            hasMetrics: !!viz.dataQuery?.metrics,
            metricsLength: viz.dataQuery?.metrics?.length,
            hasDataSet: !!viz.dataSet,
            dataSetId: viz.dataSet?.dataSetId
          });
          
          if (hasCustomQuery && noDataSetRef) {
            console.log(`🔧 [EndpointBuilder] Adding standalone visualization: ${viz.id}`);
            visualizations.push({
              id: viz.id,
              title: viz.title || viz.id,
              endpoint: this.buildVisualizationEndpoint(config.id, viz.id),
              hasCustomQuery: true
            });
          }
        });
      }
    });
    
    // Extract datasets (mirrors server logic)
    if (config.dataSets) {
      // Dashboard-level dataset
      if (config.dataSets.dashboard) {
        datasets.push({
          id: config.dataSets.dashboard.id,
          name: config.dataSets.dashboard.name,
          endpoint: config.dataSets.dashboard.source?.endpoint || this.buildDatasetEndpoint(config.dataSets.dashboard.id),
          scope: 'dashboard'
        });
      }
      
      // Tab-level datasets  
      if (config.dataSets.tabs) {
        Object.values(config.dataSets.tabs).forEach(dataset => {
          datasets.push({
            id: dataset.id,
            name: dataset.name,
            endpoint: dataset.source?.endpoint || this.buildDatasetEndpoint(dataset.id),
            scope: 'tab'
          });
        });
      }
      
      // Component-level datasets
      if (config.dataSets.components) {
        Object.values(config.dataSets.components).forEach(dataset => {
          datasets.push({
            id: dataset.id,
            name: dataset.name,
            endpoint: dataset.source?.endpoint || this.buildDatasetEndpoint(dataset.id),
            scope: 'component'
          });
        });
      }
    }
    
    // Virtual dataset from mockDataConfig (mirrors server logic)
    if (config.mockDataConfig && !datasets.find(d => d.id === `${config.id}-dataset`)) {
      datasets.push({
        id: `${config.id}-dataset`,
        name: `${config.name} Unified Dataset`,
        endpoint: this.buildDatasetEndpoint(config.id), // Use config.id, not config.id-dataset
        scope: 'dashboard'
      });
    }
    
    return { visualizations, datasets };
  }
  
  /**
   * Find the appropriate endpoint for a given analytics request
   * This helps determine which generated API endpoint to call
   */
  static findEndpointForRequest(
    config: AnalyticsConfig,
    request: {
      visualizationId?: string;
      datasetId?: string;
      configId?: string;
    }
  ): { type: 'visualization' | 'dataset'; endpoint: string; id: string } | null {
    const endpoints = this.extractEndpointsFromConfig(config);
    
    // Try to find specific visualization endpoint
    if (request.visualizationId) {
      const viz = endpoints.visualizations.find(v => v.id === request.visualizationId);
      if (viz) {
        return {
          type: 'visualization',
          endpoint: viz.endpoint,
          id: viz.id
        };
      }
    }
    
    // Try to find specific dataset endpoint
    if (request.datasetId) {
      const dataset = endpoints.datasets.find(d => d.id === request.datasetId);
      if (dataset) {
        return {
          type: 'dataset',
          endpoint: dataset.endpoint,
          id: dataset.id
        };
      }
    }
    
    // Fallback: try to find a suitable dataset endpoint
    if (endpoints.datasets.length > 0) {
      const dataset = endpoints.datasets[0]; // Use first available dataset
      return {
        type: 'dataset',
        endpoint: dataset.endpoint,
        id: dataset.id
      };
    }
    
    return null;
  }
}
